import DateTimeFormatter from "@utils/datetime.formatter";
import commentPromiseQueue from "@plugins/comment/comment.promise.queue";
import commentApi from "@apis/comment.api.ts";
import { reactive } from "vue";
import { ApiResponse } from "@apis/api.ts";
import {
  CommentResponse,
  CommentSummaryLatestResponse,
  CommentSummaryResponse,
} from "@plugins/comment/types.ts";

/**
 * This store will mainly be used by comment plugin
 */
const state = reactive({
  commentSectionStatus: {},
  commentSummaryInfo: {},
  timeStamp: null,
  lastCommentInfo: {},
});

export default {
  state: state,
  clearLastCommentInfo() {
    this.state.lastCommentInfo = {};
  },
  getAddMode(resourceUuid) {
    if (this.state.commentSectionStatus[resourceUuid] === undefined) {
      return;
    }
    return this.state.commentSectionStatus[resourceUuid].addMode;
  },
  setAddMode(resourceUuid, addMode) {
    this.state.commentSectionStatus[resourceUuid] = {
      addMode: addMode,
      editCommentId: 0,
      commentContent: "",
    };
  },
  getEditCommentId(resourceUuid) {
    if (this.state.commentSectionStatus[resourceUuid] === undefined) {
      return;
    }
    return this.state.commentSectionStatus[resourceUuid].editCommentId;
  },
  getEditCommentContent(resourceUuid) {
    if (this.state.commentSectionStatus[resourceUuid] === undefined) {
      return;
    }
    return this.state.commentSectionStatus[resourceUuid].editCommentContent;
  },
  getNewCommentTimeStamp() {
    return this.state.timeStamp;
  },
  setNewCommentTimeStamp(timeStamp) {
    this.state.timeStamp = timeStamp;
  },
  setEditComment(resourceUuid, commentId, commentContent) {
    this.state.commentSectionStatus[resourceUuid] = {
      addMode: false,
      editCommentId: commentId,
      editCommentContent: commentContent,
    };
  },
  remove(resourceUuid) {
    this.setEditComment(resourceUuid, 0, "");
  },
  reset(resourceUuid) {
    setTimeout(() => {
      delete this.state.commentSectionStatus[resourceUuid];
    }, 1);
  },
  getNumberOfComments(resourceUuid) {
    if (!this.state.commentSummaryInfo[resourceUuid]) return 0;
    else return this.state.commentSummaryInfo[resourceUuid].number;
  },
  fetchSummaryInfo(resourceUuid, resourceType, academicYear, teamId) {
    commentPromiseQueue.enqueue(() => {
      const requestData = { resourceUuid, resourceType, academicYear, teamId };
      return commentApi
        .fetchCommentSummary(requestData)
        .then((response: ApiResponse<CommentSummaryResponse>) => {
          if (response.code === "ok") {
            this.state.commentSummaryInfo[resourceUuid] = response.data;
          }
        }, this.passiveApiFailHandler);
    }, null);
  },
  fetchSummaryLatestInfo(
    resourceUuids: string[],
    resourceType: string,
    academicYear: number,
  ): Promise<void> {
    if (!resourceUuids || resourceUuids.length === 0) {
      return Promise.resolve();
    }
    const requestData = { resourceUuids, resourceType, academicYear };
    return commentApi
      .fetchCommentSummaryLatest(requestData)
      .then((response: ApiResponse<Record<string, CommentSummaryLatestResponse>>) => {
        if (response.code !== "ok") {
          return;
        }
        Object.entries(response.data).forEach(([resourceUuid, summaryLatest]) => {
          this.state.commentSummaryInfo[resourceUuid] = {
            number: summaryLatest.number,
            lastCommentTime: summaryLatest.lastCommentTime,
          };
          if (summaryLatest.latestComment) {
            this.state.lastCommentInfo[resourceUuid] = summaryLatest.latestComment;
          }
        });
      }, this.passiveApiFailHandler);
  },
  getLastCommentedTime(resourceUuid) {
    if (!this.state.commentSummaryInfo[resourceUuid]) {
      this.state.commentSummaryInfo[resourceUuid] = {};
    }
    const lastCommentedTime = this.state.commentSummaryInfo[resourceUuid].lastCommentTime;
    return lastCommentedTime
      ? DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(lastCommentedTime)
      : "";
  },
  fetchLastCommentInfo(resourceUuid, resourceType, academicYear) {
    return new Promise((resolve, reject) => {
      commentPromiseQueue.enqueue(() => {
        const requestData = { resourceUuid, resourceType, academicYear };
        commentApi.fetchLastComment(requestData).then(
          (response: ApiResponse<CommentResponse>) => {
            if (response.code === "ok") {
              this.state.lastCommentInfo[resourceUuid] = response.data;
              return resolve(response);
            }
            return reject();
          },
          (response) => {
            this.passiveApiFailHandler();
            return reject(response);
          },
        );
      }, null);
    });
  },
  // Update the last comment info based on whether it's a new comment or an edit
  updateLastCommentInfo(resourceUuid, resourceType, comment) {
    if (resourceType !== "POST") return;

    const commentStatus = this.state.commentSectionStatus[resourceUuid];
    const lastComment = this.state.lastCommentInfo[resourceUuid];

    if (commentStatus.addMode) {
      // Create a new comment
      this.state.lastCommentInfo[resourceUuid] = comment;
    } else if (commentStatus.editCommentId === lastComment?.id) {
      // Edit the last comment
      this.state.lastCommentInfo[resourceUuid] = comment;
    }
  },

  // Check if the last comment has been deleted, and fetch the latest comment if so
  deletedLastComment(data) {
    if (data.resourceType !== "POST") return;

    const lastComment = this.state.lastCommentInfo[data.resourceUuid];

    if (lastComment?.uuid === data.uuid) {
      delete this.state.lastCommentInfo[data.resourceUuid];
      this.fetchLastCommentInfo(data.resourceUuid, data.resourceType, data.academicYear);
    }
  },
};
